import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Sparkles, Package as PackageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { svcPriceForBranch, pkgPriceForBranch } from "@/lib/PackageEngine";
import { formatBranchMoney } from "@/lib/api";

// Searchable Service + Package picker.
//
// Inline dropdown (matches CustomerCombobox) so mouse-wheel scrolling works
// natively inside the results panel — earlier Popover-based cmdk version
// would swallow wheel events in some viewports.
//
// Two visual modes:
//   • Empty  → search input; typing filters services then packages
//   • Picked → a chip showing "<name>" or "Package · <name>" with an ✕ that
//              calls onChange(null)
//
// Result rows (Iter-316 — matches the Mindbody legacy layout):
//   Line 1  BOLD service / (PACKAGE) name
//   Line 2  {Duration} · {Price in branch currency} · {Category}
//
// Prices come from `svcPriceForBranch` / `pkgPriceForBranch` (PackageEngine
// — imported, never mutated) so branch-specific overrides are honoured
// exactly like the POS / invoice path. Currency formatting flows through
// `formatBranchMoney` so Bali branches render `Rp…`, Bahrain `BHD…`,
// Italy `€…`, etc. — mandatory clause 3a (Global Branch Shield).
//
// Props:
//   value    → { kind: "service" | "package", id: string } | null
//   onChange → callback with the new value object OR null on clear
//   services → [{id, name, duration_min?, pricing?, category?}]
//   packages → [{id, name, items|service_ids, pricing?, category?}]
//   branch   → optional branch doc — carries `.currency` for the price label
//   branchId → optional branch id — used by svcPriceForBranch / pkgPriceForBranch
//   testId   → data-testid prefix (default "service-combobox")
export default function ServiceCombobox({
  value,
  onChange,
  services = [],
  packages = [],
  branch = null,
  branchId = null,
  testId = "service-combobox",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Resolve the label for the current selection so the chip reads correctly.
  const label = useMemo(() => {
    if (!value) return null;
    if (value.kind === "service") return services.find((s) => s.id === value.id)?.name || null;
    if (value.kind === "package") {
      const p = packages.find((x) => x.id === value.id);
      return p ? `Package · ${p.name}` : null;
    }
    return null;
  }, [value, services, packages]);

  // Case-insensitive substring filter over both lists. Cap each at 40 so a
  // fully-empty query on catalogs with hundreds of rows still renders quickly
  // (the operator will type to narrow further).
  const q = query.trim().toLowerCase();
  const filteredServices = useMemo(() => {
    const rows = q ? services.filter((s) => (s.name || "").toLowerCase().includes(q)) : services;
    return rows.slice(0, 40);
  }, [services, q]);
  const filteredPackages = useMemo(() => {
    const rows = q ? packages.filter((p) => (p.name || "").toLowerCase().includes(q)) : packages;
    return rows.slice(0, 40);
  }, [packages, q]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (v) => { onChange(v); setQuery(""); setOpen(false); };

  // Sum an in-package service list into a single friendly duration string.
  // Packages carry `.items = [{service_id, ...}]` — we look up each service
  // in the catalog and sum their `duration_min`. Falls back to 0 (rendered
  // as empty) when the catalog is not yet loaded.
  const packageDuration = (pkg) => {
    const items = pkg?.items || pkg?.service_ids || [];
    if (!items.length || !services.length) return 0;
    let total = 0;
    for (const it of items) {
      const sid = typeof it === "string" ? it : it?.service_id;
      if (!sid) continue;
      const svc = services.find((s) => s.id === sid);
      if (svc?.duration_min) total += svc.duration_min;
    }
    return total;
  };

  if (value && label) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        data-testid={`${testId}-chip`}
      >
        {value.kind === "package"
          ? <PackageIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          : <Sparkles className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
        <span className="flex-1 min-w-0 truncate font-medium">{label}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
          aria-label="Clear service"
          data-testid={`${testId}-clear`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  const nothingFound = filteredServices.length === 0 && filteredPackages.length === 0;

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Type to search a service or package…"
          className="pl-9"
          data-testid={`${testId}-search`}
        />
      </div>
      {open && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 rounded-md border border-border bg-white shadow-lg max-h-72 overflow-y-auto overflow-x-hidden"
          data-testid={`${testId}-panel`}
        >
          {nothingFound && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No match.</div>
          )}
          {filteredServices.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Services</div>
              {filteredServices.map((s) => {
                const priceLbl = formatBranchMoney(branch || "IDR", svcPriceForBranch(s, branchId));
                const durLbl = formatDuration(s.duration_min);
                const cat = (s.category || "").trim();
                return (
                  <button
                    type="button"
                    key={`service:${s.id}`}
                    onClick={() => pick({ kind: "service", id: s.id })}
                    className={cn(
                      "w-full text-left px-3 py-2 hover:bg-secondary/60 flex items-start gap-2",
                      value?.kind === "service" && value?.id === s.id && "bg-secondary/40",
                    )}
                    data-testid={`${testId}-item-service-${s.id}`}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{s.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {[durLbl, priceLbl, cat].filter(Boolean).join(", ")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
          {filteredPackages.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground border-t border-border/60">Packages</div>
              {filteredPackages.map((p) => {
                const priceLbl = formatBranchMoney(branch || "IDR", pkgPriceForBranch(p, branchId));
                const durLbl = formatDuration(packageDuration(p));
                const cat = (p.category || "").trim();
                const itemCount = (p.items || p.service_ids || []).length;
                const meta = [durLbl, priceLbl, cat].filter(Boolean).join(", ")
                  || `${itemCount} item${itemCount === 1 ? "" : "s"}`;
                return (
                  <button
                    type="button"
                    key={`package:${p.id}`}
                    onClick={() => pick({ kind: "package", id: p.id })}
                    className={cn(
                      "w-full text-left px-3 py-2 hover:bg-secondary/60 flex items-start gap-2",
                      value?.kind === "package" && value?.id === p.id && "bg-secondary/40",
                    )}
                    data-testid={`${testId}-item-package-${p.id}`}
                  >
                    <PackageIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        <span className="text-muted-foreground font-medium">(PACKAGE)</span> {p.name}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{meta}</div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Friendly "1 hour", "1 hour 30 mins", "45 mins" style formatter that
// mirrors the Mindbody legacy label in the second reference screenshot.
function formatDuration(min) {
  const n = Number(min || 0);
  if (!n) return "";
  if (n < 60) return `${n} mins`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  const hLbl = h === 1 ? "1 hour" : `${h} hours`;
  if (m === 0) return hLbl;
  return `${hLbl} ${m} mins`;
}
